import React from 'react';
import { Spinner, Table } from 'react-bootstrap';
import { useSelector } from 'react-redux';

import Card from 'components/DesignSystem/Card';
import RelatorioTable, { formatNumberBR } from 'components/Relatorios/shared/RelatorioTable';
import RichHtml from 'components/RichHtml';
import EditButton from 'components/Shared/EditButton';

import { selectUserIsAdmin } from 'redux/reducers/authReducer';
import useIntersemanalSecaoParams from 'hooks/Relatorios/Intersemanal/useIntersemanalSecaoParams';
import { useGetIntersemanalResultadosQuery } from 'services/api/products/relatorios';
import { DiretrizStatus } from 'types/Relatorios/Intersemanal';

import './index.scss';

const STATUS_LABEL: Record<DiretrizStatus, string> = {
  normal: 'Normal',
  atencao: 'Atenção',
  alerta: 'Alerta',
  restricao: 'Restrição',
};

const STATUS_ORDER: DiretrizStatus[] = ['normal', 'atencao', 'alerta', 'restricao'];

export default function Resultados() {
  const userIsAdmin = useSelector(selectUserIsAdmin);
  const params = useIntersemanalSecaoParams();

  const { data, isLoading } = useGetIntersemanalResultadosQuery(params);

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center p-3">
        <Spinner animation="border" role="status" />
      </div>
    );
  }

  if (!data) {
    return null;
  }

  const {
    precos, ena, armazenamento, diretrizes,
  } = data;

  const armazenamentoColumns = ['Subsistema', ...armazenamento.revisoes];
  const armazenamentoRows = armazenamento.linhas.map((linha) => [
    linha.subsistema,
    ...linha.valores,
  ]);

  return (
    <div className="intersemanal-resultados">
      <Card title="Preços" className="relatorio-table">
        <Table responsive hover>
          <thead>
            <tr>
              <th rowSpan={2}>Subsistema</th>
              {precos.revisoes.map((rev) => <th key={rev} colSpan={2}>{rev}</th>)}
            </tr>
            <tr>
              {precos.revisoes.map((rev) => (
                <React.Fragment key={rev}>
                  <th>PLD (sem limite*)</th>
                  <th>PLD</th>
                </React.Fragment>
              ))}
            </tr>
          </thead>
          <tbody>
            {precos.linhas.map((linha) => (
              <tr key={linha.subsistema}>
                <td>{linha.subsistema}</td>
                {linha.valores.map((valor, i) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <React.Fragment key={`${linha.subsistema}-${i}`}>
                    <td>{formatNumberBR(valor.pld_sem_limite)}</td>
                    <td>{formatNumberBR(valor.pld)}</td>
                  </React.Fragment>
                ))}
              </tr>
            ))}
          </tbody>
        </Table>
      </Card>

      <Card title="Energia Naturalmente Afluente" className="relatorio-table">
        <p className="intersemanal-resultados__legenda">
          Cenários de ENAs mensais para cada revisão
        </p>
        <Table responsive hover>
          <thead>
            <tr>
              <th rowSpan={2}>Subsistema</th>
              {ena.revisoes.map((rev) => <th key={rev} colSpan={2}>{rev}</th>)}
            </tr>
            <tr>
              {ena.revisoes.map((rev) => (
                <React.Fragment key={rev}>
                  <th>MWmed</th>
                  <th>%MLT</th>
                </React.Fragment>
              ))}
            </tr>
          </thead>
          <tbody>
            {ena.linhas.map((linha) => (
              <tr key={linha.subsistema}>
                <td>{linha.subsistema}</td>
                {linha.valores.map((valor, i) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <React.Fragment key={`${linha.subsistema}-${i}`}>
                    <td>{formatNumberBR(valor.mw_med)}</td>
                    <td>{`${formatNumberBR(valor.pct_mlt)}%`}</td>
                  </React.Fragment>
                ))}
              </tr>
            ))}
          </tbody>
        </Table>
      </Card>

      <RelatorioTable
        titulo="Energia Armazenada (% EARmax)"
        columns={armazenamentoColumns}
        rows={armazenamentoRows}
      />

      <Card title="Diretrizes Operativas" className="relatorio-table">
        <div className="intersemanal-resultados__legenda-chips">
          <span>Legenda:</span>
          {STATUS_ORDER.map((status) => (
            <span key={status} className={`intersemanal-resultados__chip intersemanal-resultados__chip--${status}`}>
              {STATUS_LABEL[status]}
            </span>
          ))}
        </div>
        <Table responsive hover>
          <thead>
            <tr>
              <th rowSpan={2}>Usina</th>
              <th colSpan={diretrizes.meses.length}>Vazão Turbinada Máxima (m3/s)</th>
              <th colSpan={diretrizes.meses.length}>Vazão Defluente Mínima (m3/s)</th>
            </tr>
            <tr>
              {diretrizes.meses.map((mes) => <th key={`vtm-${mes}`}>{mes}</th>)}
              {diretrizes.meses.map((mes) => <th key={`vdm-${mes}`}>{mes}</th>)}
            </tr>
          </thead>
          <tbody>
            {diretrizes.linhas.map((linha) => (
              <tr key={linha.usina}>
                <td>{linha.usina}</td>
                {linha.vazao_turbinada_maxima.map((v, i) => (
                  <td
                    // eslint-disable-next-line react/no-array-index-key
                    key={`${linha.usina}-vtm-${i}`}
                    className={`intersemanal-resultados__cell intersemanal-resultados__cell--${v.status}`}
                  >
                    {formatNumberBR(v.valor)}
                  </td>
                ))}
                {linha.vazao_defluente_minima.map((v, i) => (
                  <td
                    // eslint-disable-next-line react/no-array-index-key
                    key={`${linha.usina}-vdm-${i}`}
                    className={`intersemanal-resultados__cell intersemanal-resultados__cell--${v.status}`}
                  >
                    {formatNumberBR(v.valor)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </Table>
      </Card>

      <Card
        title="Considerações sobre o cenário"
        actions={userIsAdmin && (
          <EditButton id="edit-resultados-consideracoes" onClick={() => undefined} />
        )}
      >
        <RichHtml html={data.consideracoes_html} className="w-100 ql-editor" />
      </Card>
    </div>
  );
}
